import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

const AUTO_SCROLL_EDGE_PX = 48;
const AUTO_SCROLL_SPEED_PX_PER_SEC = 180;

export default class extends Controller {
  static targets = ["card"];
  static values = {
    gapPx: Number,
    rowHeightPx: Number,
    cols: Number,
    minWidthCells: Number,
    minHeightCells: Number,
    maxHeightCells: Number,
  };

  // A card appended after initial load (the edit-page Add Content Turbo flow) is an unsaved change,
  // so arm the leave-confirmation guard. Cards present at connect are the already-saved layout.
  cardTargetConnected() {
    if (this.connected) this.dispatch("changed");
  }

  connect() {
    this.connected = true;
    this.sortable = Sortable.create(this.element, {
      animation: 150,
      chosenClass: "report-grid-card--chosen",
      dragClass: "report-grid-card--dragging",
      draggable: ".report-grid-card",
      filter: "button, a, input, textarea, select, [data-grid-no-drag]",
      ghostClass: "report-grid-card--ghost",
      handle: "[data-grid-handle]",
      forceFallback: true,
      preventOnFilter: false,
      onEnd: (event) => {
        if (event.oldIndex !== event.newIndex) this.dispatch("changed");
      },
    });
  }

  // Removal is staged like every other edit: the card just leaves the DOM (and with it, its
  // hidden `cards[][...]` fields), so Save persists the removal and Cancel/navigating away discards it.
  removeCard(event) {
    if (!window.confirm(event.params.confirm)) return;

    event.currentTarget.closest(".report-grid-card").remove();
    this.dispatch("changed");
  }

  // The canonical pencil is a full-page link to a standalone editor, so the dashboard form's current
  // state -- the title, each card's geometry and order, and any other card already staged -- would be
  // lost when the editor's staging GET re-rendered the grid from the database. Folding the form into
  // the link's query string carries it there and back. The tenant grid stages in a dialog and never
  // navigates, so it does not pass this action.
  carryStagedState(event) {
    const link = event.currentTarget;
    const form = document.getElementById("report-dashboard-form");
    if (!form) return;

    const url = new URL(link.href, window.location.origin);
    // Its own keys are the pencil's card_id and preview_tenant; the form contributes the rest.
    new FormData(form).forEach((value, key) => {
      if (key === "authenticity_token" || key === "_method") return;

      url.searchParams.append(key, value);
    });
    link.href = url.toString();
  }

  // On document, because the dialog lives in the page header rather than inside this grid.
  editLiveMetric(event) {
    const card = event.currentTarget.closest(".report-grid-card");
    document.dispatchEvent(
      new CustomEvent("report:edit-live-metric", { detail: { card } }),
    );
  }

  disconnect() {
    this.sortable?.destroy();
    this.endResize();
  }

  // The card's grabbed corner tracks the pointer in document space, so scrolling (wheel, trackpad,
  // or the edge auto-scroll below) moves the page under the pointer and the size follows steadily.
  startResize(event) {
    event.preventDefault();
    event.stopPropagation();
    this.sortable?.option("disabled", true);
    const handle = event.currentTarget;
    const card = handle.closest(".report-grid-card");
    const rect = card.getBoundingClientRect();
    this.resize = {
      card,
      handle,
      scrollContainer: this.scrollContainer(),
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      offsetX: rect.right - event.clientX,
      offsetY: rect.bottom - event.clientY,
      startW: parseInt(card.dataset.positionW, 10),
      startH: parseInt(card.dataset.positionH, 10),
      // Growing past the row's right edge would wrap the card to a new row and repack the grid.
      maxSpan: this.colsValue - this.startColumn(card, rect),
    };
    this.onMove = (e) => {
      this.resize.clientX = e.clientX;
      this.resize.clientY = e.clientY;
      this.updateResize();
    };
    this.onScroll = () => this.updateResize();
    this.onUp = () => this.endResize();
    handle.setPointerCapture?.(event.pointerId);
    document.body.classList.add("cursor-se-resize");
    window.addEventListener("pointermove", this.onMove);
    window.addEventListener("pointerup", this.onUp);
    window.addEventListener("pointercancel", this.onUp);
    window.addEventListener("scroll", this.onScroll, {
      capture: true,
      passive: true,
    });
    this.startAutoScroll();
  }

  startColumn(card, rect) {
    const { cellWidth } = this.cellMetrics();
    const styles = window.getComputedStyle(this.element);
    const contentLeft =
      this.element.getBoundingClientRect().left +
      parseFloat(styles.paddingLeft);
    return Math.round(
      (rect.left - contentLeft) / (cellWidth + this.gapPxValue),
    );
  }

  updateResize() {
    const { card, clientX, clientY, offsetX, offsetY, maxSpan } = this.resize;
    const { cellWidth, cellHeight } = this.cellMetrics();
    const rect = card.getBoundingClientRect();
    const targetWidth = clientX + offsetX - rect.left;
    const targetHeight = clientY + offsetY - rect.top;
    const span = this.snapWidthCells(
      (targetWidth + this.gapPxValue) / (cellWidth + this.gapPxValue),
      maxSpan,
    );
    const minHeightCells =
      parseInt(card.dataset.minHeightCells, 10) || this.minHeightCellsValue;
    const heightCells = this.snapHeightCells(
      targetHeight,
      cellHeight,
      minHeightCells,
    );
    const height = this.heightForCells(heightCells, cellHeight);
    card.style.gridColumn = `span ${span} / span ${span}`;
    card.style.gridRow = `span ${heightCells} / span ${heightCells}`;
    card.style.height = `${height}px`;
    card.dataset.positionW = span;
    card.dataset.positionH = height;
  }

  endResize() {
    if (!this.resize) return;
    window.removeEventListener("pointermove", this.onMove);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("pointercancel", this.onUp);
    window.removeEventListener("scroll", this.onScroll, { capture: true });
    this.stopAutoScroll();
    this.resize.handle?.releasePointerCapture?.(this.resize.pointerId);
    document.body.classList.remove("cursor-se-resize");
    this.sortable?.option("disabled", false);
    const { card, startW, startH } = this.resize;
    this.resize = null;
    this.syncPositionFields(card);
    if (
      parseInt(card.dataset.positionW, 10) !== startW ||
      parseInt(card.dataset.positionH, 10) !== startH
    ) {
      this.dispatch("changed");
    }
  }

  syncPositionFields(card) {
    card.querySelector("[data-position-field='w']").value =
      card.dataset.positionW;
    card.querySelector("[data-position-field='h']").value =
      card.dataset.positionH;
  }

  // Holding the pointer near the container edge scrolls at a slow constant speed — the rect-based
  // size math above turns that into a steady one-row-at-a-time stretch with no acceleration.
  startAutoScroll() {
    let last = null;
    const tick = (now) => {
      if (!this.resize) return;

      if (last !== null) {
        const bounds = this.scrollBounds(this.resize.scrollContainer);
        let direction = 0;

        if (bounds.bottom - this.resize.clientY < AUTO_SCROLL_EDGE_PX) {
          direction = 1;
        } else if (this.resize.clientY - bounds.top < AUTO_SCROLL_EDGE_PX) {
          direction = -1;
        }

        if (direction !== 0) {
          this.scrollBy(
            this.resize.scrollContainer,
            (direction * AUTO_SCROLL_SPEED_PX_PER_SEC * (now - last)) / 1000,
          );
          this.updateResize();
        }
      }

      last = now;
      this.autoScrollFrame = window.requestAnimationFrame(tick);
    };

    this.autoScrollFrame = window.requestAnimationFrame(tick);
  }

  stopAutoScroll() {
    if (!this.autoScrollFrame) return;

    window.cancelAnimationFrame(this.autoScrollFrame);
    this.autoScrollFrame = null;
  }

  scrollContainer() {
    let node = this.element.parentElement;

    while (node && node !== document.body) {
      if (/(auto|scroll)/.test(window.getComputedStyle(node).overflowY)) {
        return node;
      }

      node = node.parentElement;
    }

    return window;
  }

  scrollBounds(container) {
    if (container === window) return { top: 0, bottom: window.innerHeight };

    const rect = container.getBoundingClientRect();
    return { top: rect.top, bottom: rect.bottom };
  }

  scrollBy(container, amount) {
    if (container === window) {
      window.scrollBy(0, amount);
    } else {
      container.scrollTop += amount;
    }
  }

  snapHeightCells(height, cellHeight, minCells = this.minHeightCellsValue) {
    const rowHeight = cellHeight + this.gapPxValue;
    return Math.max(
      minCells,
      Math.min(
        this.maxHeightCellsValue,
        Math.round((height + this.gapPxValue) / rowHeight),
      ),
    );
  }

  snapWidthCells(width, maxSpan) {
    return Math.max(
      this.minWidthCellsValue,
      Math.min(maxSpan, Math.round(width)),
    );
  }

  heightForCells(cells, cellHeight) {
    return Math.round(cells * cellHeight + (cells - 1) * this.gapPxValue);
  }

  cellMetrics() {
    const styles = window.getComputedStyle(this.element);
    const paddingX =
      parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
    const gridWidth = this.element.getBoundingClientRect().width - paddingX;
    const cellWidth =
      (gridWidth - this.gapPxValue * (this.colsValue - 1)) / this.colsValue;
    return { gridWidth, cellWidth, cellHeight: this.rowHeightPxValue };
  }
}
